import { Flex, Space, Table, Tag, Tooltip, Button } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { IoEyeOutline } from 'react-icons/io5';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import HeartLoader from '@/shared/ui/HeartLoader';
import { formatStudyDateTime } from '@/shared/utils/studyDate';
import { Study } from '@/models/study';
import { Pagination } from '@/models';
import type { TableProps } from 'antd';
import { FaRegClipboard } from 'react-icons/fa';
import { useStudyPrefetch } from '@/state/hooks/useStudyPrefetch';
import { useSettings } from '@/features/settings';
import { StudyTagsCell } from '@/features/studies/StudyTagsCell';
import styles from './AdminStudyTable.module.css';

interface Props {
  studies?: Pagination<Study>;
  page: number;
  pageSize: number;
  loading?: boolean;
  onTableChange: TableProps<Study>['onChange'];
}

const AdminStudyTable: FC<Props> = ({
  studies,
  page,
  pageSize,
  loading,
  onTableChange,
}) => {
  const navigate = useNavigate();
  const { settings } = useSettings();

  const columns = [
    {
      title: 'Patient',
      dataIndex: 'patientFirstName',
      key: 'patientFirstName',
      sorter: true,
      render: (text: string, record: Study) => (
        <Flex gap="large" align="middle">
          <FaRegClipboard size={18} />
          <span>{`${text} ${record.patientLastName}`}</span>
        </Flex>
      ),
    },
    {
      title: 'MRN',
      dataIndex: 'patientMedicalRecordNumber',
      key: 'patientMedicalRecordNumber',
      sorter: true,
    },
    {
      title: 'Study Type',
      dataIndex: 'modality',
      key: 'modality',
      sorter: true,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: Study['priority']) => {
        if (!priority) return <Tag>—</Tag>;
        const priorityStyles: Record<
          string,
          { bg: string; color: string; border: string; icon: string }
        > = {
          STAT: {
            bg: 'var(--priority-stat-bg)',
            color: 'var(--priority-stat)',
            border: 'rgba(248, 79, 49, 0.3)',
            icon: '▲',
          },
          URGENT: {
            bg: 'var(--priority-urgent-bg)',
            color: 'var(--priority-urgent)',
            border: 'rgba(212, 160, 23, 0.3)',
            icon: '●',
          },
          ROUTINE: {
            bg: 'var(--priority-routine-bg)',
            color: 'var(--priority-routine)',
            border: 'rgba(0, 174, 239, 0.25)',
            icon: '▼',
          },
        };
        const style = priorityStyles[priority] || priorityStyles.ROUTINE;
        return (
          <Tag
            style={{
              backgroundColor: style.bg,
              color: style.color,
              border: `1px solid ${style.border}`,
            }}
          >
            {style.icon} {priority}
          </Tag>
        );
      },
      sorter: true,
    },
    {
      title: 'Assigned',
      dataIndex: 'isAssigned',
      key: 'isAssigned',
      render: (isAssigned: boolean) => (
        <Tag className={isAssigned ? styles.tagAssigned : styles.tagUnassigned}>
          {isAssigned ? 'ASSIGNED' : 'UNASSIGNED'}
        </Tag>
      ),
    },
    {
      title: 'Study Date',
      dataIndex: 'studyDate',
      key: 'studyDate',
      sorter: true,
      render: (text: string) => <span>{formatStudyDateTime(text)}</span>,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (_: unknown, record: Study) => (
        <StudyTagsCell study={record} compact={settings.compactMode} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Study) => (
        <div className="row-actions">
          <Space size={8}>
            <Tooltip title="Open full viewer">
              <Button
                type="text"
                icon={<IoEyeOutline />}
                onClick={() => navigate(`/studies/${record.uuid}`)}
              />
            </Tooltip>

            <Tooltip title="Open report only">
              <Button
                type="text"
                icon={<FileTextOutlined />}
                onClick={() => navigate(`/reports/${record.uuid}/preview`)}
              />
            </Tooltip>
          </Space>
        </div>
      ),
    },
  ];

  const prefetchStudy = useStudyPrefetch();

  return (
    <Flex vertical gap={24} className={styles.container}>
      <Table<Study>
        columns={columns}
        dataSource={studies?.results}
        rowKey={(r) => r.uuid}
        loading={{ spinning: !!loading, indicator: <HeartLoader size="sm" /> }}
        onChange={onTableChange}
        bordered={false}
        onRow={(record) => ({
          onMouseEnter: () => prefetchStudy(record.uuid),
        })}
        pagination={{
          current: page,
          pageSize,
          total: studies?.count,
          showSizeChanger: true,
          showTotal: (t, [s, e]) => `${s}-${e} of ${t}`,
        }}
      />
    </Flex>
  );
};

export default AdminStudyTable;
